import React from 'react';
import goldColors from '../themes/goldColors';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Divider, 
  useTheme, 
  useMediaQuery,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import AppShowcase, { PcmFeaturesGrid } from './AppShowcase';

// Icons
import PieChartIcon from '@mui/icons-material/PieChart';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BarChartIcon from '@mui/icons-material/BarChart';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SyncIcon from '@mui/icons-material/Sync';
import CategoryIcon from '@mui/icons-material/Category';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ReportIcon from '@mui/icons-material/Report';
import SecurityIcon from '@mui/icons-material/Security';
import LanguageIcon from '@mui/icons-material/Language';
import PeopleIcon from '@mui/icons-material/People';
import DevicesIcon from '@mui/icons-material/Devices';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const FeaturesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t, language } = useLanguage();

  // Main feature sections
  const mainFeatures = [
    {
      title: language === 'es' ? "Panel Financiero" : "Financial Dashboard",
      description: language === 'es' ? "Visión general visual completa de sus finanzas con métricas en tiempo real e indicadores clave de rendimiento." : "Comprehensive visual overview of your finances with real-time metrics and key performance indicators.",
      icon: <PieChartIcon fontSize="large" sx={{ color: goldColors.main }} />,
      details: language === 'es' ? [
        "Tarjetas resumen de ingresos, gastos y saldo",
        "Gráfico interactivo de desglose de gastos",
        "Visualización de fuentes de ingresos",
        "Seguimiento del progreso de ahorro",
        "Puntuación de salud financiera",
        "Comparación mes a mes",
        "Identifique y elimine 'gastos hormiga'"
      ] : [
        "Summary cards for income, expenses, and balance",
        "Interactive expense breakdown chart",
        "Income sources visualization",
        "Savings progress tracker",
        "Financial health score",
        "Month-over-month comparison",
        "Identify and eliminate small recurring expenses"
      ]
    },
    {
      title: language === 'es' ? "Vista de Flujo de Caja en Calendario" : "Calendar Cashflow View",
      description: language === 'es' ? "Visualice su actividad financiera diaria con una interfaz de calendario intuitiva que muestra ingresos, gastos y saldo." : "Visualize your daily financial activity with an intuitive calendar interface showing income, expenses, and balance.",
      icon: <CalendarMonthIcon fontSize="large" sx={{ color: goldColors.main }} />,
      details: language === 'es' ? [
        "Ingresos (azul) y gastos (gris) con código de color",
        "Seguimiento de saldo diario en verde/rojo",
        "Visión general mensual con totales acumulados",
        "Notificaciones de próximas facturas e ingresos",
        "Programa tus gastos sobre la base de tus saldos diarios futuros",
        "Vista detallada del día con lista de transacciones"
      ] : [
        "Color-coded income (blue) and expenses (grey)",
        "Daily balance tracking in green/red",
        "Monthly overview with running totals",
        "Upcoming bill and income notifications",
        "Schedule your expenses based on your future daily balances",
        "Detailed day view with transaction list"
      ]
    },
    {
      title: language === 'es' ? "Herramientas de Planificación" : "Planning Tools",
      description: language === 'es' ? "Establezca metas financieras y cree planes detallados para alcanzarlas con nuestras herramientas de planificación integral." : "Set financial goals and create detailed plans to achieve them with our comprehensive planning tools.",
      icon: <TuneIcon fontSize="large" sx={{ color: goldColors.main }} />,
      details: language === 'es' ? [
        "Creación y gestión de presupuestos",
        "Establecimiento de metas con seguimiento de progreso",
        "Planificación de escenarios hipotéticos",
        "Calculadora de jubilación",
        "Planificador de pagos de deudas",
        "Constructor de fondo de emergencia"
      ] : [
        "Budget creation and management",
        "Goal setting with progress tracking",
        "What-if scenario planning",
        "Retirement calculator",
        "Debt payoff planner",
        "Emergency fund builder"
      ]
    }
  ];

  // PCM Framework features
  const pcmFeatures = [
    {
      title: language === 'es' ? "Planificación" : "Planning",
      description: language === 'es' ? "Cree planes financieros detallados para lograr sus objetivos a corto y largo plazo." : "Create detailed financial plans to achieve your short and long-term goals.",
      icon: <AccountBalanceIcon fontSize="large" sx={{ color: goldColors.main }} />,
      points: language === 'es' ? [
        "Creación de presupuestos personalizados",
        "Planes de ahorro basados en objetivos",
        "Herramientas de proyección de ingresos",
        "Previsión de gastos",
        "Preparación para eventos financieros significativos"
      ] : [
        "Personalized budget creation",
        "Goal-based savings plans",
        "Income projection tools",
        "Expense forecasting",
        "Preparation for significant financial events"
      ]
    },
    {
      title: language === 'es' ? "Control" : "Controlling",
      description: language === 'es' ? "Tome el control de sus finanzas con herramientas que ayudan a administrar y optimizar su dinero." : "Take control of your finances with tools that help manage and optimize your money.",
      icon: <SettingsIcon fontSize="large" sx={{ color: goldColors.main }} />,
      points: language === 'es' ? [
        "Límites de gastos basados en categorías",
        "Programación de pagos de facturas",
        "Gestión de transacciones recurrentes",
        "Alertas de saldo de cuenta",
        "Asignación de cartera de inversiones"
      ] : [
        "Category-based spending limits",
        "Bill payment scheduling",
        "Recurring transaction management",
        "Account balance alerts",
        "Investment portfolio allocation"
      ]
    },
    {
      title: language === 'es' ? "Monitoreo" : "Monitoring",
      description: language === 'es' ? "Realice un seguimiento de su desempeño financiero en comparación con sus planes mediante herramientas de monitoreo detalladas." : "Track your financial performance against your plans with detailed monitoring tools.",
      icon: <ShowChartIcon fontSize="large" sx={{ color: goldColors.main }} />,
      points: language === 'es' ? [
        "Seguimiento de transacciones en tiempo real",
        "Análisis de presupuesto frente a resultados reales",
        "Identificación de tendencias de gasto",
        "Monitoreo del progreso hacia objetivos",
        "Indicadores de salud financiera"
      ] : [
        "Real-time transaction tracking",
        "Budget vs. actual analysis",
        "Spending trend identification",
        "Goal progress monitoring",
        "Financial health indicators"
      ]
    }
  ];

  // Additional features
  const additionalFeatures = [
    {
      title: language === 'es' ? "Soporte Multilingüe" : "Multilingual Support",
      description: language === 'es' ? "Acceda a la plataforma en varios idiomas según su preferencia" : "Access the platform in multiple languages to suit your preference",
      icon: <LanguageIcon sx={{ color: goldColors.main }} />,
      details: language === 'es' ? "Opciones de idioma en inglés, español y portugués" : "English, Spanish, and Portuguese language options"
    },
    {
      title: language === 'es' ? "Consultas con Expertos Financieros" : "Financial Expert Consultations",
      description: language === 'es' ? "Obtenga asesoramiento personalizado de expertos financieros reales" : "Get personalized advice from real financial experts",
      icon: <PeopleIcon sx={{ color: goldColors.main }} />,
      details: language === 'es' ? "Asesoramiento financiero personalizado" : "Personalized financial advice"
    },
    {
      title: language === 'es' ? "Portal de Cliente Seguro" : "Secure Client Portal",
      description: language === 'es' ? "Acceda a su información financiera a través de un sistema de inicio de sesión seguro" : "Access your financial information through a secure login system",
      icon: <SecurityIcon sx={{ color: goldColors.main }} />,
      details: language === 'es' ? "Autenticación de dos factores y conexión segura (HTTPS)" : "Two-factor authentication and secure connection (HTTPS)"
    },
    {
      title: language === 'es' ? "Sincronización Multidispositivo" : "Multi-device Synchronization",
      description: language === 'es' ? "Acceda a sus datos financieros desde cualquier dispositivo, en cualquier lugar" : "Access your financial data from any device, anywhere",
      icon: <DevicesIcon sx={{ color: goldColors.main }} />,
      details: language === 'es' ? "Diseño responsivo que funciona en escritorio, tablet y móvil" : "Responsive design works on desktop, tablet, and mobile"
    },
    {
      title: language === 'es' ? "Multi Moneda y Multi Cuentas" : "Multi-Currency and Multi-Account",
      description: language === 'es' ? "Gestione múltiples cuentas y diferentes divisas en una sola plataforma" : "Manage multiple accounts and different currencies in a single platform",
      icon: <CloudSyncIcon sx={{ color: goldColors.main }} />,
      details: language === 'es' ? "Soporte para múltiples divisas con conversión automática de tasas" : "Support for multiple currencies with automatic rate conversion"
    },
    {
      title: language === 'es' ? "Categorías Personalizadas" : "Custom Categories",
      description: language === 'es' ? "Cree sus propias categorías de transacciones que se adapten a su estilo de vida" : "Create your own transaction categories that match your lifestyle",
      icon: <CategoryIcon sx={{ color: goldColors.main }} />,
      details: language === 'es' ? "Categorías con código de color con reglas y filtros personalizados" : "Color-coded categories with custom rules and filters"
    }
  ];

  // Feature comparison - Básico, Profesional, y Gurú
  const featureComparison = language === 'es' ? [
    { name: "Panel Financiero", basico: true, profesional: true, guru: true },
    { name: "Vista de Flujo de Caja en Calendario", basico: true, profesional: true, guru: true },
    { name: "Categorías de Transacciones", basico: "Estándar", profesional: "Personalizado", guru: "Personalizado" },
    { name: "Ingreso manual de transacciones", basico: true, profesional: true, guru: true },
    { name: "Ingreso de transacciones multi canal", basico: false, profesional: true, guru: true },
    { name: "Herramientas de gestión de deudas", basico: true, profesional: true, guru: true },
    { name: "Informes Financieros", basico: "Básico", profesional: "Avanzado", guru: "Avanzado" },
    { name: "Alertas y notificaciones", basico: false, profesional: true, guru: true },
    { name: "Seguimiento de Objetivos", basico: "3 objetivos", profesional: "Ilimitado", guru: "Ilimitado" },
    { name: "Importador de estados de cuenta", basico: false, profesional: true, guru: true },
    { name: "Soporte por correo electrónico", basico: false, profesional: true, guru: true },
    { name: "Cuentas familiares colaborativas", basico: false, profesional: false, guru: true },
    { name: "Monitoreo de Inversiones", basico: false, profesional: false, guru: true },
    { name: "Indicadores financieros avanzados", basico: false, profesional: false, guru: true },
    { name: "Consulta personalizada mensual", basico: false, profesional: false, guru: true }
  ] : [
    { name: "Financial Dashboard", basic: true, pro: true, guru: true },
    { name: "Cash Flow Calendar View", basic: true, pro: true, guru: true },
    { name: "Transaction Categories", basic: "Standard", pro: "Custom", guru: "Custom" },
    { name: "Manual transaction entry", basic: true, pro: true, guru: true },
    { name: "Multi-channel transaction entry", basic: false, pro: true, guru: true },
    { name: "Debt management tools", basic: true, pro: true, guru: true },
    { name: "Financial Reports", basic: "Basic", pro: "Advanced", guru: "Advanced" },
    { name: "Budget alerts and notifications", basic: false, pro: true, guru: true },
    { name: "Goal Tracking", basic: "3 goals", pro: "Unlimited", guru: "Unlimited" },
    { name: "Statement importer tool", basic: false, pro: true, guru: true },
    { name: "Email Support", basic: false, pro: true, guru: true },
    { name: "Collaborative family accounts", basic: false, pro: false, guru: true },
    { name: "Investment Monitoring", basic: false, pro: false, guru: true },
    { name: "Advanced financial indicators", basic: false, pro: false, guru: true },
    { name: "Monthly personalized consultation", basic: false, pro: false, guru: true }
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ mb: 8, textAlign: 'center' }}>
          <Typography
            variant="h3"
            component="h1"
            fontWeight="bold"
            gutterBottom
            sx={{ 
              fontSize: { xs: '2rem', md: '3rem' },
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {language === 'es' ? 'Potentes Funciones para el Éxito Financiero' : 'Powerful Features for Financial Success'}
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
          >
            {language === 'es' ? 'Nuestro completo conjunto de herramientas financieras le ayuda a planificar, controlar y monitorear sus finanzas con facilidad.' : 'Our comprehensive suite of financial tools helps you plan, control, and monitor your finances with ease.'}
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={RouterLink}
            to="/register"
            endIcon={<ArrowForwardIcon sx={{ color: goldColors.main }} />}
          >
            {language === 'es' ? 'COMENZAR AHORA' : 'GET STARTED NOW'}
          </Button>
        </Box>

        {/* Pantallas reales y todo lo que hace PCM hoy -- oct 2026 */}
        <Box sx={{ mb: 10 }}>
          <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1 }}>
            {language === 'es' ? 'Así se ve PCM por dentro' : 'What PCM looks like inside'}
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 3 }}>
            {language === 'es' ? 'Pantallas reales de la app, con datos de ejemplo.' : 'Real app screens, with sample data.'}
          </Typography>
          <Box sx={{ maxWidth: 860, mx: 'auto', mb: 8 }}>
            <AppShowcase language={language} light />
          </Box>
          <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
            {language === 'es' ? 'Todo lo que hace PCM hoy' : 'Everything PCM does today'}
          </Typography>
          <PcmFeaturesGrid language={language} />
        </Box>

        {/* Main Features */}
        <Box sx={{ mb: 10 }}>
          <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
            {language === 'es' ? 'Características Principales' : 'Core Features'}
          </Typography>
          
          <Grid container spacing={4}>
            {mainFeatures.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 6
                    }
                  }}
                >
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom align="center" fontWeight="medium">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph align="center">
                    {feature.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <List dense>
                      {feature.details.map((detail, idx) => (
                        <ListItem key={idx} disableGutters alignItems="flex-start">
                          <ListItemIcon sx={{ minWidth: 30, mt: 0.5 }}>
                            <CheckCircleIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={detail} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* PCM Framework */}
        <Box sx={{ mb: 10, bgcolor: 'background.default', py: 6, px: 3, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" align="center">
            {language === 'es' ? 'Descubra el Poder de la Planificación, Control y Monitoreo Financiero (PCM)' : 'Discover the Power of Financial Planning, Controlling and Monitoring (PCM)'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3, maxWidth: 800, mx: 'auto' }}>
            {language === 'es' ? 'Nuestro completo conjunto de herramientas le ayuda a tomar el control de sus finanzas a través de nuestra probada metodología PCM (Planificación, Control, Monitoreo). Desde presupuestos sólidos hasta informes detallados, le proporcionamos todo lo que necesita para lograr la libertad financiera.' : 
            'Our comprehensive suite of tools helps you take control of your finances through our proven PCM (Planning, Controlling, Monitoring) methodology. From robust budgeting to detailed reporting, we provide everything you need to achieve financial freedom.'}
          </Typography>
          
          <Grid container spacing={4}>
            {pcmFeatures.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card 
                  elevation={3}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: `1px solid ${theme.palette.divider}`,
                    '&:hover': { boxShadow: 6 }
                  }}
                >
                  <Box 
                    sx={{ 
                      p: 2, 
                      bgcolor: 'background.paper',
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    {feature.icon}
                    <Typography variant="h5" fontWeight="medium">
                      {feature.title}
                    </Typography>
                  </Box>
                  <CardContent sx={{ flexGrow: 1, pt: 2, pb: 2 }}>
                    <Typography variant="body2" paragraph color="text.secondary">
                      {feature.description}
                    </Typography>
                    {/* Simple list with explicit left alignment */}
                    <Box sx={{ mt: 2, pl: 1, width: '100%' }}>
                      {feature.points.map((point, idx) => (
                        <Box 
                          key={idx} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            mb: 1.5,
                            width: '100%',
                            justifyContent: 'flex-start'
                          }}
                        >
                          <CheckCircleIcon 
                            sx={{ 
                              color: goldColors.main, 
                              fontSize: '0.9rem',
                              minWidth: '20px',
                              mr: 1,
                              mt: '2px',
                              flexShrink: 0
                            }} 
                          />
                          <Typography 
                            variant="body2"
                            align="left"
                            sx={{ 
                              width: '100%',
                              textAlign: 'left !important',
                              fontSize: '0.875rem',
                              lineHeight: 1.3
                            }}
                          >
                            {point}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Additional Features */}
        <Box sx={{ mb: 10 }}>
          <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
            {language === 'es' ? 'Características Adicionales' : 'Additional Features'}
          </Typography>
          
          <Grid container spacing={3}>
            {additionalFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    p: 2,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'background.default' }
                  }}
                >
                  <Box 
                    sx={{ 
                      mr: 2, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 45,
                      height: 45,
                      borderRadius: '50%',
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText'
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom fontSize="1.1rem">
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {feature.description}
                    </Typography>
                    <Typography variant="body2" color="primary.main" fontWeight="medium">
                      {feature.details}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Feature Comparison */}
        <Box sx={{ mb: 10 }}>
          <Typography variant="h4" gutterBottom align="center">
            {language === 'es' ? 'Comparación de Planes' : 'Plan Comparison'}
          </Typography>
          
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Table Header */}
            <Grid container sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 2 }}>
              <Grid item xs={3}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {language === 'es' ? 'Característica' : 'Feature'}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {language === 'es' ? 'Básico' : 'Basic'}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {language === 'es' ? 'Profesional' : 'Pro'}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {language === 'es' ? 'Gurú' : 'Guru'}
                </Typography>
              </Grid>
            </Grid>
            
            {/* Table Body */}
            {featureComparison.map((feature, index) => {
              const planKeys = language === 'es' ? ['basico', 'profesional', 'guru'] : ['basic', 'pro', 'guru'];
              
              return (
                <Grid 
                  container 
                  key={index} 
                  sx={{ 
                    p: 2, 
                    bgcolor: index % 2 === 0 ? 'background.paper' : 'background.default',
                    borderBottom: index < featureComparison.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                  }}
                >
                  <Grid item xs={3}>
                    <Typography variant="body2">
                      {feature.name}
                    </Typography>
                  </Grid>
                  
                  {planKeys.map(plan => (
                    <Grid item xs={3} key={plan} sx={{ textAlign: 'center' }}>
                      {typeof feature[plan] === 'boolean' ? (
                        feature[plan] ? 
                          <CheckCircleIcon color="success" fontSize="small" /> : 
                          <Typography variant="body2" color="text.secondary">—</Typography>
                      ) : (
                        <Typography variant="body2">{feature[plan]}</Typography>
                      )}
                    </Grid>
                  ))}
                </Grid>
              );
            })}
          </Paper>
          
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/#pricing"
              color="primary"
            >
              {language === 'es' ? t('features.getStarted', 'Comenzar') : 'View Pricing Plans'}
            </Button>
          </Box>
        </Box>

        {/* CTA Section */}
        <Box 
          sx={{ 
            p: 4, 
            borderRadius: 2, 
            textAlign: 'center',
            background: `linear-gradient(45deg, ${theme.palette.primary.main}66, ${theme.palette.secondary.main}66)`,
          }}
        >
          <Typography variant="h5" gutterBottom>
            {language === 'es' ? '¿Listo para tomar el control de sus finanzas?' : 'Ready to Take Control of Your Finances?'}
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 3 }}>
            {language === 'es' ? 'Deja de adivinar en qué se va tu dinero: ordena tus finanzas y recupera el control.' : 'Stop guessing where your money goes: get your finances in order and take back control.'}
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={RouterLink}
            to="/register"
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.9)'
              }
            }}
          >
            {language === 'es' ? 'Crea Tu Cuenta Hoy' : 'Create Your Account Today'}
          </Button>
        </Box>
        
      </Container>
    </Box>
  );
};

export default FeaturesPage;
